import { KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard } from 'lucide-react';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { cn } from 'utils/classnames';
import { useAppStore, useProductStore, useReceiptStore, useTrackingStore } from 'store';
import { SCREEN_TYPES } from 'utils/constants';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { BarcodeType, ProductLine } from 'types';
import { playCallbackSound, playErrorSound, playGoodSound } from 'utils/sounds';
import { toast } from 'sonner';

export const UpcInputField = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { currentScreen, setCurrentScreen, setBarcodeDebugMessage } = useAppStore();
  const { receiptData } = useReceiptStore();
  const { setCurrentProduct, setProductNew, setProductCondition, setProductId, productId } =
    useProductStore();
  const { setCurrentTrackingId } = useTrackingStore();

  const inResult = currentScreen !== SCREEN_TYPES.SCAN_ITEM;

  useEffect(() => {
    setProductId('');
    const interval = setInterval(() => {
      const el = inputRef.current;
      if (el && !el.disabled && document.activeElement !== el) el.focus();
    }, 200);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onItemScanned = (product: ProductLine): void => {
    setCurrentProduct(product);
    setCurrentScreen(SCREEN_TYPES.CONFIRM_ITEM);
  };

  const handleSubmit = () => {
    const trimmedProductId = productId.trim();

    if (!trimmedProductId) {
      toast.warning(t('toast.pleaseEnterProduct'));
      return;
    }

    const barcodeType = isInputBarcode(trimmedProductId) as BarcodeType | null;
    if (barcodeType) {
      setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
      getBarcodeAction(barcodeType, currentScreen, {
        setProductNew,
        setProductCondition,
        setBarcodeDebugMessage,
        setProductId: () => {},
        setTrackingId: setCurrentTrackingId,
        setCurrentProduct,
      });
      setProductId('');
      return;
    }

    const normalizedProductId = trimmedProductId.toLowerCase();
    const lines = (receiptData?.lines || receiptData?.receipt?.lines || []) as ProductLine[];

    const matchedLine = lines.find((line) => {
      if (!line) return false;
      const upcMatch = line.upc && line.upc.toString().trim().toLowerCase() === normalizedProductId;
      const productIdMatch =
        line.product_id && line.product_id.toString().trim().toLowerCase() === normalizedProductId;
      const isOpen = line.line_grade_status !== 'completed';
      return (upcMatch || productIdMatch) && isOpen;
    });

    if (matchedLine) {
      const isCallback =
        matchedLine.exception_process === 'callback' ||
        matchedLine.disposition_stage === 'callback';
      isCallback ? playCallbackSound() : playGoodSound();
      setCurrentProduct(matchedLine);
      if (currentScreen === SCREEN_TYPES.SCAN_ITEM) {
        setCurrentScreen(SCREEN_TYPES.CONFIRM_ITEM);
      }
    } else {
      // Case 1: Product not in receipt - create unknown product
      const unknownProduct: ProductLine = {
        transaction_id: receiptData?.transaction_id || receiptData?.receipt?.transaction_id,
        receipt_line: null,
        product_id: 'Unknown',
        style_color: 'N/A',
        product_size: 'N/A',
        expected_ind: 'n',
        upc: trimmedProductId,
        style_image_url: null,
        line_grade_status: 'open',
      };
      setProductId('');
      onItemScanned(unknownProduct);
      playErrorSound();
    }
  };

  return (
    <div className="flex items-center gap-2.5">
      <Input
        ref={inputRef}
        id="upc-input"
        type="text"
        inputMode={keyboardVisible ? 'text' : 'none'}
        placeholder={t('scanItem.inputPlaceholder')}
        value={productId}
        onChange={(e) => setProductId(e.target.value)}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
          }
        }}
        onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
        autoComplete="off"
        spellCheck={false}
        className={cn('text-base', inResult && 'bg-white/95 border-white/30')}
      />
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn(
          'h-12 w-12 shrink-0 rounded-xl',
          inResult ? 'bg-white/90 border-white/30' : 'border-slate-300'
        )}
        onClick={toggleKeyboardInput}
        aria-pressed={keyboardVisible}
      >
        <Keyboard className="h-5 w-5" />
      </Button>
    </div>
  );
};
